// Property document upload helpers — shared between the standalone Documentos
// section on the detail page and the in-form dropzone on Add/Edit Property.
//
// Flow per file:
//   1. document-presign-put edge function → signed R2 PUT URL
//   2. fetch PUT bytes → R2
//   3. record_property_document RPC → property_documents row
//
// All three layers (this client, the edge function, the RPC) cap at 20 MB and
// accept the same mime allowlist. Keep them in sync if any of the bounds
// change.

import { supabase } from './supabase';
import { putToR2 } from './upload-r2';

export const PROPERTY_DOC_MAX_BYTES = 20 * 1024 * 1024;

export const PROPERTY_DOC_ALLOWED_MIME = new Set<string>([
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
]);

// Fallback extension → mime map so files that arrive with empty `file.type`
// (Safari + some macOS Office docs) still pass validation when their extension
// is unambiguous. We only use it when file.type is missing.
const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
};

export function resolveMimeType(file: File): string {
  if (file.type && PROPERTY_DOC_ALLOWED_MIME.has(file.type)) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_TO_MIME[ext] ?? file.type ?? '';
}

export interface DocValidationError {
  reason: 'size' | 'mime';
  message: string;
}

export function validatePropertyDoc(file: File): DocValidationError | null {
  if (file.size > PROPERTY_DOC_MAX_BYTES) {
    return {
      reason: 'size',
      message: `"${file.name}" excede los 20 MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
    };
  }
  const mime = resolveMimeType(file);
  if (!PROPERTY_DOC_ALLOWED_MIME.has(mime)) {
    return {
      reason: 'mime',
      message: `Tipo de archivo no permitido: ${file.type || file.name.split('.').pop() || 'desconocido'}`,
    };
  }
  return null;
}

export interface UploadResult {
  ok: boolean;
  filename: string;
  error?: string;
}

// supabase-js's functions.invoke surfaces a FunctionsHttpError whose
// `.message` is the unhelpful generic "Edge Function returned a non-2xx
// status code". The real error message lives in the Response body attached
// at `.context`. This reads the body and falls back through several known
// error-payload shapes (our own functions return `{ error, message? }`;
// supabase-js sometimes attaches a `details` field). Returns a friendly
// Spanish default when nothing useful is present.
async function extractFunctionError(err: any, fallback: string): Promise<string> {
  if (!err) return fallback;
  // FunctionsHttpError attaches the Response on `.context`. It can only be
  // read once, so we clone before consuming.
  const ctx = (err as any).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.clone().json();
      if (typeof body === 'string') return body;
      if (body?.error && body?.message) return `${body.error}: ${body.message}`;
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      try {
        const text = await ctx.clone().text();
        if (text) return text;
      } catch { /* fall through */ }
    }
  }
  return err.message && err.message !== 'Edge Function returned a non-2xx status code'
    ? err.message
    : fallback;
}

// Translate the raw English error from our edge function / RPC into the
// friendlier Spanish copy users actually want to see. Falls through with
// the original message if we don't have a translation.
function localizeUploadError(raw: string): string {
  if (/Forbidden.*not the assigned agent/i.test(raw)) {
    return 'No tienes permiso para subir documentos a esta propiedad. Solo el agente asignado o un administrador de la agencia pueden hacerlo.';
  }
  if (/Property not found or no access/i.test(raw)) {
    return 'No tienes acceso a esta propiedad o ya fue eliminada.';
  }
  if (/Unsupported contentType/i.test(raw)) {
    return 'Tipo de archivo no permitido por el servidor.';
  }
  if (/Size must be/i.test(raw)) {
    return 'El archivo excede el límite de 20 MB.';
  }
  if (/Unauthorized/i.test(raw)) {
    return 'Tu sesión expiró. Vuelve a iniciar sesión e inténtalo de nuevo.';
  }
  return raw;
}

export async function uploadPropertyDocument(propertyId: string, file: File): Promise<UploadResult> {
  if (!supabase) return { ok: false, filename: file.name, error: 'Supabase no inicializado' };

  const validation = validatePropertyDoc(file);
  if (validation) return { ok: false, filename: file.name, error: validation.message };

  const contentType = resolveMimeType(file);

  try {
    const { data: presignData, error: presignErr } = await supabase.functions.invoke(
      'document-presign-put',
      { body: { propertyId, filename: file.name, contentType, size: file.size } },
    );
    if (presignErr) {
      const raw = await extractFunctionError(presignErr, 'Error al firmar la subida');
      return { ok: false, filename: file.name, error: localizeUploadError(raw) };
    }
    if (!presignData?.uploadUrl || !presignData?.storagePath) {
      return { ok: false, filename: file.name, error: 'Respuesta de presign inválida' };
    }

    try {
      await putToR2(presignData.uploadUrl, file, contentType);
    } catch (err) {
      return { ok: false, filename: file.name, error: (err as Error).message };
    }

    const { error: rpcErr } = await supabase.rpc('record_property_document', {
      p_property_id: propertyId,
      p_storage_path: presignData.storagePath,
      p_name: file.name,
      p_size_bytes: file.size,
      p_mime_type: contentType,
    });
    if (rpcErr) return { ok: false, filename: file.name, error: localizeUploadError(rpcErr.message) };

    return { ok: true, filename: file.name };
  } catch (err: any) {
    return { ok: false, filename: file.name, error: localizeUploadError(err?.message ?? 'Error al subir') };
  }
}

export async function uploadPropertyDocuments(propertyId: string, files: File[]): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  for (const file of files) {
    results.push(await uploadPropertyDocument(propertyId, file));
  }
  return results;
}

export interface DownloadResult {
  ok: boolean;
  downloadUrl?: string;
  error?: string;
}

// Localize the document-presign-get error responses. The function returns
// "Document not found or no access" when RLS rejects the row — same message
// for "deleted" and "not yours", since RLS treats them identically.
function localizeDownloadError(raw: string): string {
  if (/Document not found or no access/i.test(raw)) {
    return 'No tienes acceso a este documento o ya fue eliminado.';
  }
  if (/Unauthorized/i.test(raw)) {
    return 'Tu sesión expiró. Vuelve a iniciar sesión e inténtalo de nuevo.';
  }
  return raw;
}

export async function getPropertyDocumentDownloadUrl(documentId: string): Promise<DownloadResult> {
  if (!supabase) return { ok: false, error: 'Supabase no inicializado' };
  try {
    const { data, error } = await supabase.functions.invoke('document-presign-get', {
      body: { documentId },
    });
    if (error) {
      const raw = await extractFunctionError(error, 'Error al obtener URL de descarga');
      return { ok: false, error: localizeDownloadError(raw) };
    }
    if (!data?.downloadUrl) return { ok: false, error: 'Respuesta sin URL de descarga' };
    return { ok: true, downloadUrl: data.downloadUrl };
  } catch (err: any) {
    return { ok: false, error: localizeDownloadError(err?.message ?? 'Error al descargar') };
  }
}
